import { useEffect, useState, useCallback } from "react";
import { Nav, Tab } from "react-bootstrap";
import { PermissionService } from "@services/permissions/permissionService";
import type { Feature } from "@services/permissions/commands/cmdCreateFeature";
import type { Role } from "@services/permissions/commands/cmdListRoles";
import FeaturesSection from "./components/FeaturesSection";
import RolesSection from "./components/RolesSection";
import showAlert from "src/shared/utils/generic.tsx";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import "./styles.scss";

const permissionService = new PermissionService();

export default function PermissionsPage() {
  useDocumentTitle("Permissions");
  const [features, setFeatures] = useState<Feature[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("features");

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    try {
      const [f, r] = await Promise.all([
        permissionService.listFeatures(),
        permissionService.listRoles(),
      ]);
      setFeatures(f);
      setRoles(r);
    } catch {
      showAlert("Failed to load permissions data", "error", 3000);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Feature CRUD
  async function createFeature(name: string, parentId: number | null) {
    await permissionService.createFeature({ name, parent_id: parentId });
    setFeatures(await permissionService.listFeatures());
  }

  async function updateFeature(id: number, data: { name?: string; parent_id?: number | null }) {
    await permissionService.updateFeature(id, data);
    setFeatures(await permissionService.listFeatures());
  }

  async function deleteFeature(id: number) {
    await permissionService.deleteFeature(id);
    setFeatures(await permissionService.listFeatures());
  }

  // Role CRUD
  async function createRole(name: string) {
    await permissionService.createRole({ name });
    setRoles(await permissionService.listRoles());
  }

  async function renameRole(id: number, name: string) {
    await permissionService.updateRole(id, { name });
    setRoles(await permissionService.listRoles());
  }

  async function deleteRole(id: number) {
    await permissionService.deleteRole(id);
    setRoles(await permissionService.listRoles());
  }

  async function savePermissions(roleId: number, permissions: { feature_id: number; level: number }[]) {
    await permissionService.setRolePermissions(roleId, permissions);
    showAlert("Permissions saved", "success", 3000);
  }

  return (
    <div className="permissions-page">
      <Tab.Container activeKey={activeTab} onSelect={(k) => setActiveTab(k || "features")}>
        <Nav variant="tabs" className="permissions-page__tabs">
          <Nav.Item>
            <Nav.Link eventKey="features">Features</Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link eventKey="roles">Roles</Nav.Link>
          </Nav.Item>
        </Nav>

        <Tab.Content>
          <Tab.Pane eventKey="features">
            <FeaturesSection
              features={features}
              isLoading={isLoading}
              onCreate={createFeature}
              onUpdate={updateFeature}
              onDelete={deleteFeature}
            />
          </Tab.Pane>
          <Tab.Pane eventKey="roles">
            <RolesSection
              roles={roles}
              features={features}
              isLoading={isLoading}
              onCreate={createRole}
              onRename={renameRole}
              onDelete={deleteRole}
              onGetRole={(id) => permissionService.getRole(id)}
              onSavePermissions={savePermissions}
            />
          </Tab.Pane>
        </Tab.Content>
      </Tab.Container>
    </div>
  );
}
